/**
 * Playback.
 *
 * The old behaviour, and why it had to go: `voice()` returned the sampler if it
 * happened to be loaded and the synthesiser otherwise, freshly decided on every
 * play. So the first Listen after launch played a modelled piano, the third
 * played a recorded one, and nothing told the learner why the instrument had
 * changed underneath them. Worse, a failed load set `unavailable` for the rest
 * of the session with no way back.
 *
 * The rules now:
 *
 *   - The sampled instrument is the normal voice. Loading starts as soon as the
 *     shell is ready, not on the first tap.
 *   - A play request *waits* for readiness, with a bounded timeout, rather than
 *     quietly substituting a different instrument.
 *   - The voice is chosen once per play and passed through, so changing
 *     instrument mid-arpeggio cannot re-route the notes still to come.
 *   - The synthesiser is an explicit, named degraded mode with a Retry, reached
 *     only by a real failure or a real timeout.
 *   - One load promise per instrument, so React's double-mount, a second tap and
 *     a retry all join the same load instead of starting three.
 *
 * iOS will not start audio without a user gesture, so nothing is constructed
 * until the first play call and `unlock()` is wired to the first tap.
 */

import * as Tone from 'tone';
import { frequency, type Pitch } from '../theory/pitch';
import {
  INSTRUMENTS,
  sampleBaseUrl,
  sampleUrls,
  type InstrumentId,
} from './instruments';
import { createFallbackVoice, type Voice } from './voices';

export type PlayMode = 'block' | 'arpeggio' | 'both';
export type Timbre = InstrumentId;

/**
 * What the learner is told, in their language.
 *
 * `degraded` is the only state that mentions the synthesiser, and it never
 * appears because samples merely have not finished — only because they failed
 * or took too long.
 */
export type LoadState = 'idle' | 'loading' | 'ready' | 'degraded';

/** How long a play request will wait for samples before sounding degraded. */
export const READY_TIMEOUT_MS = 6000;

/** Above this many voices, the oldest is released. Mobile Safari is the limit. */
const MAX_POLYPHONY = 24;

interface Loader {
  state: LoadState;
  promise: Promise<Tone.Sampler | null> | null;
  sampler: Tone.Sampler | null;
  attempts: number;
}

let reverb: Tone.Reverb | null = null;
let started = false;
let currentTimbre: Timbre = 'piano';

const loaders: Record<Timbre, Loader> = {
  piano: { state: 'idle', promise: null, sampler: null, attempts: 0 },
  guitar: { state: 'idle', promise: null, sampler: null, attempts: 0 },
};

/** Physically-modelled fallbacks, built on demand and kept per instrument. */
const fallbacks: Partial<Record<Timbre, Voice>> = {};
const listeners = new Set<() => void>();
/** Notes currently sounding, oldest first, so polyphony can be capped. */
let sounding: { at: number; release: () => void }[] = [];

function notify() {
  for (const listener of listeners) listener();
}

export function onAudioStatusChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function audioStatus(timbre: Timbre = currentTimbre): LoadState {
  return loaders[timbre].state;
}

/** True once this instrument has been heard as itself rather than as a model. */
export function isSampled(timbre: Timbre = currentTimbre): boolean {
  return loaders[timbre].state === 'ready';
}

function ensureReverb(): Tone.Reverb {
  if (!reverb) reverb = new Tone.Reverb({ decay: 2.4, wet: 0.24 }).toDestination();
  return reverb;
}

function ensureFallback(timbre: Timbre): Voice {
  let voice = fallbacks[timbre];
  if (!voice) {
    voice = createFallbackVoice(timbre, ensureReverb());
    fallbacks[timbre] = voice;
  }
  return voice;
}

/**
 * Load an instrument's samples. One promise per instrument, ever — a second
 * caller joins the first rather than starting a second download.
 */
export function loadInstrument(timbre: Timbre): Promise<Tone.Sampler | null> {
  const loader = loaders[timbre];
  if (loader.sampler && loader.state === 'ready') return Promise.resolve(loader.sampler);
  if (loader.promise) return loader.promise;

  const instrument = INSTRUMENTS[timbre];
  loader.state = 'loading';
  loader.attempts += 1;
  notify();

  loader.promise = new Promise<Tone.Sampler | null>((resolve) => {
    let settled = false;
    const finish = (sampler: Tone.Sampler | null, state: LoadState) => {
      if (settled) return;
      settled = true;
      loader.sampler = sampler;
      loader.state = state;
      loader.promise = null;
      notify();
      resolve(sampler);
    };

    let sampler: Tone.Sampler;
    try {
      sampler = new Tone.Sampler({
        urls: sampleUrls(instrument),
        baseUrl: sampleBaseUrl(instrument),
        release: instrument.release,
        onload: () => finish(sampler, 'ready'),
        onerror: () => {
          sampler.dispose();
          finish(null, 'degraded');
        },
      }).connect(ensureReverb());
    } catch {
      finish(null, 'degraded');
      return;
    }

    // Instrument-specific level rather than one shared chain: a nylon guitar and
    // a grand piano do not sit at the same place in a mix.
    // Perceived loudness matters more than equal numbers here. The piano bank's
    // recorded peaks sit lower than the nylon guitar, so equal gain made every
    // instrument switch feel like a volume jump. These values were balanced as
    // a pair, with a little headroom left for a six-note chord.
    sampler.volume.value = timbre === 'guitar' ? -3.5 : -1.5;

    // A bounded wait. Without it a stalled request leaves the learner staring at
    // "Preparing…" forever, which is worse than an honest degraded mode.
    window.setTimeout(() => {
      if (!settled) {
        sampler.dispose();
        finish(null, 'degraded');
      }
    }, READY_TIMEOUT_MS);
  });

  return loader.promise;
}

/** Start loading in the background as soon as the shell is up. */
export function prepareAudio(timbre: Timbre = currentTimbre): void {
  void loadInstrument(timbre);
}

/** Try a degraded instrument again. Only a deliberate action reaches this. */
export function retryInstrument(timbre: Timbre = currentTimbre): Promise<unknown> {
  const loader = loaders[timbre];
  if (loader.state === 'loading') return loader.promise ?? Promise.resolve(null);
  loader.state = 'idle';
  loader.sampler = null;
  notify();
  return loadInstrument(timbre);
}

export async function unlock(): Promise<void> {
  if (started) return;
  await Tone.start();
  started = true;
  prepareAudio(currentTimbre);
}

export function isUnlocked(): boolean {
  return started;
}

export function setTimbre(timbre: Timbre) {
  if (timbre === currentTimbre) return;
  currentTimbre = timbre;
  prepareAudio(timbre);
  notify();
}

export function getTimbre(): Timbre {
  return currentTimbre;
}

interface Playable {
  triggerAttackRelease: (
    notes: number | number[],
    duration: number,
    time?: number,
    velocity?: number,
  ) => unknown;
  releaseAll?: (time?: number) => void;
}

/** A short musical breath between the arpeggio and the final block. */
export const ARPEGGIO_BLOCK_GAP_SECONDS = 0.12;

/**
 * Decide, once, what this play will sound like.
 *
 * Returned as a value rather than read from module state at each note, so an
 * instrument change halfway through an arpeggio cannot send the rest of it
 * through a different voice.
 */
async function voiceFor(timbre: Timbre): Promise<{ instrument: Playable; sampled: boolean }> {
  const loader = loaders[timbre];
  if (loader.state !== 'degraded') {
    const sampler = await loadInstrument(timbre);
    if (sampler) return { instrument: sampler as unknown as Playable, sampled: true };
  }
  return { instrument: ensureFallback(timbre) as unknown as Playable, sampled: false };
}

/** Keep a lid on ringing tails so repeated taps cannot overload mobile Safari. */
function remember(release: () => void) {
  sounding.push({ at: Tone.now(), release });
  while (sounding.length > MAX_POLYPHONY) sounding.shift()?.release();
}

/**
 * Play chord tones separately so the bass can carry the foundation without
 * turning the whole sample bank up. A single array attack gives Tone one
 * velocity for every pitch and is why the bottom of guitar chords disappeared.
 */
function playWeightedChord(
  instrument: Playable,
  freqs: number[],
  duration: number,
  time: number,
  upperVelocity = 0.78,
) {
  [...freqs]
    .sort((a, b) => a - b)
    .forEach((frequency, index) => {
      const velocity = index === 0 ? 1 : index === 1 ? 0.88 : upperVelocity;
      instrument.triggerAttackRelease(frequency, duration, time, velocity);
    });
}

export async function playPitches(
  pitches: Pitch[],
  mode: PlayMode = 'block',
  bpm = 96,
): Promise<void> {
  if (!pitches.length) return;

  // Read *before* the first await, not after. `currentTimbre` can change while
  // the unlock or the load is in flight, and the notes already promised belong
  // to the instrument that was showing when the learner pressed Listen — not to
  // whichever one they picked while waiting.
  const timbre = currentTimbre;
  await unlock();
  const { instrument } = await voiceFor(timbre);

  const now = Tone.now();
  const step = 60 / bpm / 2;
  const freqs = pitches.map(frequency).sort((a, b) => a - b);

  if (mode === 'block') {
    playWeightedChord(instrument, freqs, 1.9, now);
    remember(() => instrument.releaseAll?.());
    return;
  }

  freqs.forEach((f, i) => {
    const velocity = i === 0 ? 0.86 : 0.7;
    instrument.triggerAttackRelease(
      f,
      mode === 'both' ? step * 1.08 : 1.1,
      now + i * step,
      velocity,
    );
  });
  remember(() => instrument.releaseAll?.());

  if (mode === 'both') {
    const blockAt = now + freqs.length * step + ARPEGGIO_BLOCK_GAP_SECONDS;
    // Never hard-cut the premium sample tail at the seam. The short arpeggio
    // naturally decays into a weighted block, which sounds like one phrase
    // instead of two playback engines fighting each other.
    playWeightedChord(instrument, freqs, 1.9, blockAt, 0.76);
  }
}

/**
 * Strum a guitar voicing low string to high.
 *
 * A guitar chord is not six simultaneous notes, and playing it as one is the
 * clearest tell that a sampled guitar is being driven like a keyboard. The
 * spread is fixed rather than random: these are theory exercises, and two
 * playings of the same chord have to be comparable.
 */
export const STRUM_SPREAD_MS = 22;

export async function playVoicing(
  pitches: Pitch[],
  options: { strum?: boolean } = {},
): Promise<void> {
  if (!pitches.length) return;
  if (options.strum === false) return playPitches(pitches, 'block');

  const timbre = currentTimbre;
  await unlock();
  const { instrument } = await voiceFor(timbre);
  const now = Tone.now();
  const spread = STRUM_SPREAD_MS / 1000;

  // Low to high, which is the direction a downstroke actually sounds.
  const ordered = [...pitches].sort((a, b) => frequency(a) - frequency(b));
  ordered.forEach((pitch, i) => {
    // A down-stroke begins with the physical bass strings. Give that first
    // contact enough weight to remain audible on iPad speakers.
    const velocity = i === 0 ? 1 : i === 1 ? 0.9 : Math.max(0.72, 0.86 - i * 0.025);
    instrument.triggerAttackRelease(frequency(pitch), 2.4, now + i * spread, velocity);
  });
  remember(() => instrument.releaseAll?.());
}

export async function playPitch(pitch: Pitch, duration = 0.9): Promise<void> {
  const timbre = currentTimbre;
  await unlock();
  const { instrument } = await voiceFor(timbre);
  instrument.triggerAttackRelease(frequency(pitch), duration, Tone.now());
  remember(() => instrument.releaseAll?.());
}

export function stop() {
  for (const fallback of Object.values(fallbacks)) fallback?.releaseAll();
  for (const loader of Object.values(loaders)) loader.sampler?.releaseAll();
  sounding = [];
}

/** Test seam: forget every loader and voice. Not used by the app. */
export function __resetAudioForTests() {
  for (const id of Object.keys(loaders) as Timbre[]) {
    loaders[id] = { state: 'idle', promise: null, sampler: null, attempts: 0 };
    delete fallbacks[id];
  }
  sounding = [];
  started = false;
  currentTimbre = 'piano';
}
